import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { createRetension, getRetensionDays } from "../../../../api/retensionApis";
import logger from "../../../../utils/logger";
import { DEFAULT_COURT_SETTINGS } from "./RetentionSetting.constants";
import type {
  CourtRetentionSettings,
  RetentionDraft,
  RetentionDraftMap,
} from "./RetentionSetting.types";
import RetentionSettingsTab from "./RetentionSettingsTab";

interface RetentionApi {
  courtType: string;
  retentionDays?: number | string;
  retentionTimeUnit?: string;
  description?: string;
  isActive?: boolean | string;
}

interface ApiResponse<T = unknown> {
  data?: {
    status?: boolean;
    message?: string;
    data?: T;
  };
}

const RetentionSetting: React.FC = () => {
  const [courtSettings, setCourtSettings] = useState<CourtRetentionSettings[]>(
    DEFAULT_COURT_SETTINGS,
  );
  const [editingCourt, setEditingCourt] = useState<string | null>(null);
  const [tempEditData, setTempEditData] = useState<RetentionDraftMap>({});
  const [isSavingCourt, setIsSavingCourt] = useState(false);

  useEffect(() => {
    const fetchRetention = async () => {
      try {
        const res = (await getRetensionDays()) as ApiResponse<RetentionApi[]>;

        if (res?.data?.status && Array.isArray(res.data.data)) {
          const list = res.data.data;

          setCourtSettings((prev) =>
            prev.map((setting) => {
              const match = list.find(
                (retention) => retention.courtType === setting.courtType,
              );

              return match
                ? {
                    ...setting,
                    retentionDays: Number(match.retentionDays) || 0,
                    retentionTimeUnit: match.retentionTimeUnit || "",
                    description: match.description || "",
                    isActive:
                      match.isActive === true || match.isActive === "true",
                  }
                : setting;
            }),
          );
        }
      } catch (err) {
        logger.error("Failed to load retention", err);
      }
    };

    fetchRetention();
  }, []);

  const handleCourtSettingUpdate = (
    id: string,
    field: keyof CourtRetentionSettings,
    value: RetentionDraft[keyof RetentionDraft],
  ) => {
    setTempEditData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleEditCourtChange = (id: string | null) => {
    if (id === null && editingCourt) {
      setTempEditData((prev) => {
        const next = { ...prev };
        delete next[editingCourt];
        return next;
      });
    }

    setEditingCourt(id);
  };

  const updateDuration = async (id: string) => {
    const setting = courtSettings.find((court) => court._id === id);
    if (!setting) return;

    setIsSavingCourt(true);

    const editedData = tempEditData[id] || {};
    const retentionDays = editedData.retentionDays ?? setting.retentionDays;
    const retentionTimeUnit =
      editedData.retentionTimeUnit ?? setting.retentionTimeUnit;
    const description = editedData.description ?? setting.description;
    const isActive = editedData.isActive ?? setting.isActive;

    const payload = {
      courtType: setting.courtType,
      retentionDays: retentionDays.toString(),
      retentionTimeUnit,
      description: description || "",
      isActive,
    };

    try {
      const res = (await createRetension(payload)) as ApiResponse;

      if (res?.data?.status) {
        setCourtSettings((prev) =>
          prev.map((court) =>
            court._id === id
              ? {
                  ...court,
                  retentionDays: Number(retentionDays) || 0,
                  retentionTimeUnit,
                  description: String(description || ""),
                  isActive: Boolean(isActive),
                  lastUpdated: new Date().toISOString().split("T")[0],
                }
              : court,
          ),
        );

        toast.success("Updated successfully");
        setEditingCourt(null);
        setTempEditData((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else {
        toast.error(res?.data?.message || "Failed to update");
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error updating retention",
      );
    } finally {
      setIsSavingCourt(false);
    }
  };

  return (
    <RetentionSettingsTab
      courtSettings={courtSettings}
      editingCourt={editingCourt}
      tempEditData={tempEditData}
      isSavingCourt={isSavingCourt}
      onEditCourtChange={handleEditCourtChange}
      onCourtSettingUpdate={handleCourtSettingUpdate}
      onUpdateDuration={updateDuration}
    />
  );
};

export default RetentionSetting;
